import firstValue from "../../helpers/first.value";
import Container from "../../components/element/container";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Icon from "../../components/element/icon";

import formatNumber from "../../helpers/format.number";

import { getConsignList } from "../../redux/reducer/consign.reducer";
import ConsignRecord from "./components/consign.record";
import PaymentMethodModal from "../purchases/modals/payment.method.modal";
import InspectionPaymentModal from "./modals/inspection.payment.modal";

import useRequireAuth from "../../components/hook/use.require.auth";


export default function Consignments() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const { isAuthenticated, requireAuth } = useRequireAuth();

  const stage = searchParams.get("stage") || "";

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [confirmConsign, setConfirmConsign] = useState(null);

  const { consignList, consignListLoading } = useSelector(
    (state) => state.consign,
  );

  const initialLoadStarted = useRef(false);



  const refreshConsign = useCallback(async () => {
    setLoadError("");

    try {
      await dispatch(
        getConsignList({
          status: stage,
        }),
      ).unwrap();
    } catch (error) {
      setLoadError(
        error?.response?.data?.message || "Unable to load your consignments.",
      );
    } finally {
      setIsInitialLoading(false);
    }
  }, [dispatch, stage]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsInitialLoading(false);
      requireAuth();
      return;
    }

    if (initialLoadStarted.current) return;

    initialLoadStarted.current = true;
    setIsInitialLoading(true);
    refreshConsign();
  }, [isAuthenticated, refreshConsign, requireAuth]);

  return (
    <>
      <main className={`purchase-journey`}>
        <header className="purchase-page-header">
          <button
            type="button"
            className="_button purchase-page-back"
            onClick={() => navigate("/profile")}
            aria-label="Back to profile"
          >
            <Icon icon="ep:back" />
          </button>

          <h1 className="_label purchase-page-title">{t("My Consignments")}</h1>

          <article />
        </header>

        <section className="purchase-list">
          {isInitialLoading || consignListLoading ? (
            <Container tag="div" className="purchase-list-loading" aria-label="Loading orders">
              {Array.from({ length: 2 }, (_, index) => (
                <span key={index} />
              ))}
            </Container>
          ) : loadError ? (
            <Container tag="div" className="purchase-list-state">
              <Icon icon="solar:danger-triangle-linear" />

              <p>{loadError}</p>

              <button type="button" onClick={() => refreshConsign()}>
                {t("Try Again")}
              </button>
            </Container>
          ) : consignList.length === 0 ? (
            <Container tag="div" className="purchase-list-state">
              <Icon icon="solar:clipboard-list-linear" />

              <p>{t("No consignment found.")}</p>
            </Container>
          ) : (
            consignList.map((consign, consignIndex) => (
              <Container tag="div"
                className="purchase-list-entry"
                key={`${consign.id}-${consignIndex}`}
              >
                <ConsignRecord consign={consign} onPay={setConfirmConsign} />
              </Container>
            ))
          )}
        </section>
      </main>

      {confirmConsign && (
        <InspectionPaymentModal
          open
          consign={confirmConsign}
          onClose={() => setConfirmConsign(null)}
          onConfirm={() => {
            setPaymentOrder({
              orderId: firstValue(
                confirmConsign.inspection_order?.order_id,
                confirmConsign.inspection_order?.id,
                confirmConsign.inspection_order_id,
              ),
              kind: "inspection",
              record: confirmConsign,
              reservationId: confirmConsign.inspection_order?.reference_no || confirmConsign.reference_no,
              paymentAmount: `RM ${formatNumber(firstValue(
                confirmConsign.inspection_order?.amount,
                confirmConsign.inspection_order?.total,
                confirmConsign.inspection_fee,
                300,
              ))}`,
            });
            setConfirmConsign(null);
          }}
        />
      )}

      {paymentOrder && (
        <PaymentMethodModal
          open
          order={paymentOrder}
          serviceAgreementAccepted
          onClose={() => setPaymentOrder(null)}
          onSuccess={() => refreshConsign()}
        />
      )}
    </>
  );
}
